// Equipe.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import api from '../../services/api';
import toast, { Toaster } from 'react-hot-toast';
import { UserPlus, X, Trash2, Edit, Calendar, Hash, ShieldCheck, UserCircle, Mail, Image as ImageIcon, Check, Lock, ChevronLeft, ChevronRight, Plus, Settings, GripVertical } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import '../../styles/Equipe.css';

const Equipe = ({ estabelecimentoId }) => {
    const fileInputRef = useRef(null);
    const [funcionarios, setFuncionarios] = useState([]);
    const [lojaLogo, setLojaLogo] = useState('');
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editandoId, setEditandoId] = useState(null);
    const [flippedCards, setFlippedCards] = useState({});

    // --- CATEGORIAS VINCULADAS AO BANCO DE DADOS ---
    const [categorias, setCategorias] = useState([]); // [{id, nome, ordem}]
    const [showCatModal, setShowCatModal] = useState(false);
    const [novaCatNome, setNovaCatNome] = useState('');
    const [editandoCatId, setEditandoCatId] = useState(null);

    // --- CONTROLE DE PLANO E LIMITES DE EQUIPE ---
    const [limiteEquipeMax, setLimiteEquipeMax] = useState(2);

    // --- ESTADOS DE FILTRO POR SETOR E PAGINAÇÃO ---
    const [filtroSetor, setFiltroSetor] = useState('todos'); 
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 6;

    // --- ESTADOS DO RECORTE (CROP) ---
    const [image, setImage] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [showCropper, setShowCropper] = useState(false);
    
    const [imagePreview, setImagePreview] = useState(null);
    const [uploadFile, setUploadFile] = useState(null);
    
    // --- ESTADOS DE EXCLUSÃO ---
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemParaExcluir, setItemParaExcluir] = useState(null);

    const initialState = { 
        nome: '', 
        email: '', 
        whatsapp:'',
        cargo: 'Atendente', 
        senha: '', 
        data_nascimento: '', 
        ativo: 1 
    };
    const [novoMembro, setNovoMembro] = useState(initialState);

    const carregarDados = async () => {
        if (!estabelecimentoId) return;
        try {
            const [resEquipe, resLoja, resCats] = await Promise.all([
                api.get(`/cardapio/equipe/${estabelecimentoId}`),
                api.get(`/cardapio/estabelecimento/${estabelecimentoId}`),
                api.get(`/cardapio/equipe/categorias/${estabelecimentoId}`)
            ]);
            
            setFuncionarios(Array.isArray(resEquipe.data) ? resEquipe.data : []);
            setLojaLogo(resLoja.data?.logo_url || '');
            setCategorias(Array.isArray(resCats.data) ? resCats.data : []);

            // Define cargo padrão inicial no modal se houver categorias
            if (resCats.data && resCats.data.length > 0 && !novoMembro.cargo) {
                setNovoMembro(prev => ({ ...prev, cargo: resCats.data[0].nome }));
            }

            // Determina o limite de equipe com base no limite de produtos ou plano da loja
            const limiteProd = Number(resLoja.data?.limite_produtos || 50);
            if (limiteProd <= 50) {
                setLimiteEquipeMax(2);     
            } else if (limiteProd <= 100) {
                setLimiteEquipeMax(10);    
            } else if (limiteProd <= 300) {
                setLimiteEquipeMax(20);    
            } else {
                setLimiteEquipeMax(50);    
            }

        } catch (err) { 
            toast.error("Erro ao sincronizar dados."); 
        } finally { 
            setLoading(false); 
        }
    };

    useEffect(() => { carregarDados(); }, [estabelecimentoId]);

    const isOnline = (ultimoLogin) => {
        if (!ultimoLogin) return false;
        const dezMinutosAtras = new Date(Date.now() - 10 * 60 * 1000);
        return new Date(ultimoLogin) > dezMinutosAtras;
    };

    // --- FUNÇÕES DE GERENCIAMENTO DE CATEGORIAS NO BD ---
    const salvarCategoria = async (e) => {
        e.preventDefault();
        if (!novaCatNome.trim()) return;

        try {
            if (editandoCatId !== null) {
                await api.put(`/cardapio/equipe/categorias/${editandoCatId}`, { nome: novaCatNome.trim() });
                toast.success("Categoria atualizada com sucesso!");
            } else {
                await api.post(`/cardapio/equipe/categorias/${estabelecimentoId}`, { nome: novaCatNome.trim() });
                toast.success("Nova categoria criada!");
            }

            setNovaCatNome('');
            setEditandoCatId(null);
            carregarDados();
        } catch (err) {
            toast.error("Erro ao salvar categoria.");
        }
    };

    const excluirCategoria = async (catId, catNome) => {
        if (categorias.length <= 1) {
            return toast.error("Você precisa manter pelo menos uma categoria.");
        }
        try {
            await api.delete(`/cardapio/equipe/categorias/${catId}`);
            toast.success("Categoria removida.");
            if (filtroSetor.toLowerCase() === catNome.toLowerCase()) {
                setFiltroSetor('todos');
            }
            carregarDados();
        } catch (err) {
            toast.error("Erro ao excluir categoria.");
        }
    };

    // --- FUNÇÃO DE DRAG AND DROP (REORDENAÇÃO) ---
    const handleDragEnd = async (result) => {
        if (!result.destination) return;

        const items = Array.from(categorias);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        // Atualiza estado local imediatamente para fluidez visual
        setCategorias(items);

        // Prepara payload mapeando a nova ordem dos índices
        const payload = items.map((cat, index) => ({ id: cat.id, ordem: index }));

        try {
            await api.put(`/cardapio/equipe/categorias/ordenar/${estabelecimentoId}`, { itens: payload });
            toast.success("Ordem atualizada!");
        } catch (err) {
            toast.error("Erro ao salvar nova ordem.");
            carregarDados(); // Reverte em caso de falha
        }
    };

    // --- FUNÇÕES DO RECORTE ---
    const onCropComplete = useCallback((_area, pixels) => {
        setCroppedAreaPixels(pixels);
    }, []);

    const finalizarRecorte = async () => {
        try {
            const canvas = document.createElement('canvas');
            const img = new Image();
            img.src = image;
            await new Promise((res) => (img.onload = res));

            canvas.width = croppedAreaPixels.width;
            canvas.height = croppedAreaPixels.height;
            const ctx = canvas.getContext('2d');

            ctx.drawImage(
                img,
                croppedAreaPixels.x, croppedAreaPixels.y,
                croppedAreaPixels.width, croppedAreaPixels.height,
                0, 0,
                croppedAreaPixels.width, croppedAreaPixels.height
            );

            canvas.toBlob((blob) => {
                const file = new File([blob], "avatar.jpg", { type: "image/jpeg" });
                setUploadFile(file);
                setImagePreview(URL.createObjectURL(blob));
                setShowCropper(false);
            }, 'image/jpeg');
        } catch (e) {
            toast.error("Erro ao processar imagem.");
        }
    };

    const handleSalvar = async (e) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append('estabelecimento_id', estabelecimentoId);
        formData.append('nome', novoMembro.nome);
        formData.append('email', novoMembro.email);
        formData.append('whatsapp', novoMembro.whatsapp);
        formData.append('cargo', novoMembro.cargo);
        formData.append('data_nascimento', novoMembro.data_nascimento || '');
        formData.append('ativo', novoMembro.ativo);
        
        if (novoMembro.senha) formData.append('senha', novoMembro.senha);
        if (uploadFile) formData.append('foto', uploadFile);

        try {
            if (editandoId) {
                await api.put(`/cardapio/equipe/${editandoId}`, formData);
                toast.success("Cartão digital atualizado!");
            } else {
                if (funcionarios.length >= limiteEquipeMax) {
                    return toast.error(`Limite de ${limiteEquipeMax} membros atingido para o seu plano atual.`);
                }
                await api.post(`/cardapio/equipe`, formData);
                toast.success("Cartão digital emitido!");
            }
            
            fecharModal();
            carregarDados();
        } catch (err) { 
            toast.error("Erro ao salvar dados."); 
        }
    };
        
    const executarExclusao = async () => {
        try {
            await api.delete(`/cardapio/equipe/${itemParaExcluir.id}`);
            toast.success("Acesso removido com sucesso!");
            setShowDeleteModal(false);
            setItemParaExcluir(null);
            carregarDados();
        } catch (err) {
            toast.error("Erro ao remover membro da equipe.");
        }
    };
                
    const confirmarExclusao = (func) => {
        setItemParaExcluir(func);
        setShowDeleteModal(true);
    };
    
    const fecharModal = () => {
        setShowModal(false);
        setEditandoId(null);
        setNovoMembro({ ...initialState, cargo: categorias[0]?.nome || 'Atendente' });
        setImagePreview(null);
        setUploadFile(null);
        setImage(null);
        setShowCropper(false);
    };

    // --- FILTRAGEM E PAGINAÇÃO ---
    const funcionariosFiltrados = funcionarios.filter(func => {
        if (filtroSetor === 'todos') return true;
        return func.cargo?.toLowerCase() === filtroSetor.toLowerCase();
    });

    const totalPages = Math.ceil(funcionariosFiltrados.length / itemsPerPage) || 1;
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentFuncionarios = funcionariosFiltrados.slice(startIndex, startIndex + itemsPerPage);

    if (loading) return <div className="loader-container"><div className="spinner"></div></div>;

    return (
        <div className="tab-content animate-fade-in">
            <Toaster position="top-right" />
            
            <div className="top-info" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px' }}>
                <div>
                    <h2 className="content-title">Equipe Digital ({funcionarios.length}/{limiteEquipeMax})</h2>
                    <p>Controle de atendentes, gerentes e categorias customizadas.</p>
                </div>
                
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button 
                        className="btn-confirm" 
                        style={{ background: '#334155' }}
                        onClick={() => setShowCatModal(true)}
                    >
                        <Settings size={18} style={{marginRight: '8px'}} /> Gerenciar Categorias
                    </button>
                    <button 
                        className="btn-confirm" 
                        onClick={() => {
                            if (funcionarios.length >= limiteEquipeMax) {
                                toast.error(`Seu plano atual permite até ${limiteEquipeMax} membros. Faça o upgrade para adicionar mais.`);
                                return;
                            }
                            setShowModal(true);
                        }}
                    >
                        <UserPlus size={18} style={{marginRight: '8px'}} /> Novo Cartão
                    </button>
                </div>
            </div>

            {/* ABAS / SELETORES DO BANCO DE DADOS */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', borderBottom: '1px solid #e2e8f0', paddingBottom: '10px', overflowX: 'auto' }}>
                <button
                    onClick={() => { setFiltroSetor('todos'); setCurrentPage(1); }}
                    style={{
                        background: filtroSetor === 'todos' ? '#ea1d2c' : '#f8fafc',
                        color: filtroSetor === 'todos' ? '#fff' : '#64748b',
                        border: '1px solid #e2e8f0',
                        padding: '8px 16px',
                        borderRadius: '20px',
                        fontSize: '13px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        transition: '0.2s'
                    }}
                >
                    Todos os Membros
                </button>
                {categorias.map(cat => (
                    <button
                        key={cat.id}
                        onClick={() => { setFiltroSetor(cat.nome.toLowerCase()); setCurrentPage(1); }}
                        style={{
                            background: filtroSetor === cat.nome.toLowerCase() ? '#ea1d2c' : '#f8fafc',
                            color: filtroSetor === cat.nome.toLowerCase() ? '#fff' : '#64748b',
                            border: '1px solid #e2e8f0',
                            padding: '8px 16px',
                            borderRadius: '20px',
                            fontSize: '13px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            textTransform: 'capitalize',
                            transition: '0.2s'
                        }}
                    >
                        {cat.nome}
                    </button>
                ))}
            </div>

            {/* GRID DE MEMBROS */}
            {currentFuncionarios.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '50px', background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', color: '#94a3b8' }}>
                    <p>Nenhum membro encontrado nesta categoria.</p>
                </div>
            ) : (
                <div className="equipe-grid">
                    {currentFuncionarios.map(func => {
                        const online = isOnline(func.ultimo_login);
                        return (
                            <div 
                                key={func.id} 
                                className={`card-flip-container ${flippedCards[func.id] ? 'flipped' : ''}`} 
                                onClick={() => setFlippedCards(p => ({...p, [func.id]: !p[func.id]}))}
                            >
                                <div className="card-front">
                                    {lojaLogo && <img src={lojaLogo} alt="watermark" className="card-watermark" />}
                                    <div className="card-chip"></div>
                                    <button className="btn-edit-card" onClick={(e) => {
                                        e.stopPropagation();
                                        setEditandoId(func.id);
                                        const dataFormatada = func.data_nascimento ? func.data_nascimento.split('T')[0] : '';
                                        setNovoMembro({ ...func, data_nascimento: dataFormatada, senha: '' });
                                        setImagePreview(func.foto_url);
                                        setShowModal(true);
                                    }}>
                                        <Edit size={20}/>
                                    </button>
                                    
                                    <div className="card-user-info">
                                        <div className={`card-avatar-wrapper ${online ? 'status-online' : 'status-offline'}`}>
                                            <div style={{width:'100%', height:'100%', borderRadius:'50%', overflow:'hidden', background:'#1e293b'}}>
                                                {func.foto_url ? (
                                                    <img src={func.foto_url} alt="avatar" style={{width:'100%', height:'100%', objectFit:'cover'}} />
                                                ) : (
                                                    <UserCircle size={45} color="#94a3b8" />
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="card-name">{func.nome}</div>
                                            <div style={{fontSize: '11px', color:'#cbd5e1', marginTop:'2px'}}>{func.cargo?.toUpperCase()}</div>
                                        </div>
                                    </div>

                                    <div style={{marginTop: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end'}}>
                                        <div>
                                            <div className="card-label">DATA DE EMISSÃO</div>
                                            <div className="card-date">{new Date(func.criado_em).toLocaleDateString()}</div>
                                        </div>
                                        <div className="badge-status-card" style={{background: func.ativo ? '#22c55e' : '#ef4444'}}>
                                            {func.ativo ? 'ATIVO' : 'BLOQUEADO'}
                                        </div>
                                    </div>
                                </div>

                                <div className="card-back">
                                    <h4 style={{borderBottom: '1px solid #eee', paddingBottom: '5px', marginBottom: '10px'}}>Dados de Segurança</h4>
                                    <div style={{fontSize: '13px', display: 'flex', flexDirection: 'column', gap: '10px'}}>
                                        <div style={{display:'flex', alignItems:'center', gap: '8px'}}><Mail size={16} /> {func.email}</div>
                                        <div style={{display:'flex', alignItems:'center', gap: '8px'}}><whatsapp size={14} />Whatsapp: {func.whatsapp}</div>
                                        <div style={{display:'flex', alignItems:'center', gap: '8px'}}><Calendar size={16} /> Nasc: {func.data_nascimento ? new Date(func.data_nascimento).toLocaleDateString() : '---'}</div>
                                        <div style={{display:'flex', alignItems:'center', gap: '8px'}}><ShieldCheck size={16} /> Acesso: {func.ativo ? 'Liberado' : 'Suspenso'}</div>
                                        <div style={{display:'flex', alignItems:'center', gap: '8px'}}><Hash size={16} /> ID: {func.id}</div>
                                    </div>
                                    <button className="btn-delete-card-back" onClick={(e) => {
                                        e.stopPropagation();
                                        confirmarExclusao(func);
                                    }}>
                                        <Trash2 size={16} /> Remover Acesso
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* CONTROLES DE PAGINAÇÃO */}
            {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '15px', marginTop: '30px' }}>
                    <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', opacity: currentPage === 1 ? 0.5 : 1 }}
                    >
                        <ChevronLeft size={20} color="#334155" />
                    </button>
                    
                    <span style={{ fontSize: '14px', fontWeight: '700', color: '#334155' }}>
                        Página {currentPage} de {totalPages}
                    </span>

                    <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', opacity: currentPage === totalPages ? 0.5 : 1 }}
                    >
                        <ChevronRight size={20} color="#334155" />
                    </button>
                </div>
            )}

            {/* MODAL DE GERENCIAR CATEGORIAS COM DRAG AND DROP */}
            {showCatModal && (
                <div className="modal-overlay" style={{zIndex: 3500}}>
                    <div className="modal" style={{maxWidth: '450px', padding: '25px'}}>
                        <div className="modal-header-flex" style={{marginBottom: '20px'}}>
                            <h2>Gerenciar Categorias</h2>
                            <button onClick={() => { setShowCatModal(false); setEditandoCatId(null); setNovaCatNome(''); }} className="btn-close-modal-x"><X size={24}/></button>
                        </div>

                        <form onSubmit={salvarCategoria} style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                            <input 
                                className="input" 
                                style={{ flex: 1, margin: 0 }}
                                placeholder="Nome da nova categoria..." 
                                value={novaCatNome} 
                                onChange={e => setNovaCatNome(e.target.value)} 
                                required 
                            />
                            <button type="submit" className="btn-confirm" style={{ padding: '0 20px' }}>
                                {editandoCatId !== null ? 'Salvar' : <Plus size={20} />}
                            </button>
                        </form>

                        <DragDropContext onDragEnd={handleDragEnd}>
                            <Droppable droppableId="categorias-list">
                                {(provided) => (
                                    <div 
                                        {...provided.droppableProps} 
                                        ref={provided.innerRef}
                                        style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '250px', overflowY: 'auto' }}
                                    >
                                        {categorias.map((cat, index) => (
                                            <Draggable key={cat.id.toString()} draggableId={cat.id.toString()} index={index}>
                                                {(provided, snapshot) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            padding: '10px 14px',
                                                            background: snapshot.isDragging ? '#f1f5f9' : '#f8fafc',
                                                            borderRadius: '10px',
                                                            border: '1px solid #e2e8f0',
                                                            boxShadow: snapshot.isDragging ? '0 4px 6px -1px rgba(0,0,0,0.1)' : 'none',
                                                            ...provided.draggableProps.style
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                            <div {...provided.dragHandleProps} style={{ cursor: 'grab', color: '#94a3b8', display: 'flex', alignItems: 'center' }}>
                                                                <GripVertical size={18} />
                                                            </div>
                                                            <span style={{ fontWeight: '600', fontSize: '14px', color: '#334155' }}>{cat.nome}</span>
                                                        </div>
                                                        <div style={{ display: 'flex', gap: '8px' }}>
                                                            <button 
                                                                type="button" 
                                                                onClick={() => { setEditandoCatId(cat.id); setNovaCatNome(cat.nome); }}
                                                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
                                                            >
                                                                <Edit size={16} />
                                                            </button>
                                                            <button 
                                                                type="button" 
                                                                onClick={() => excluirCategoria(cat.id, cat.nome)}
                                                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }}
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    </div>
                </div>
            )}

            {/* MODAL PRINCIPAL (CADASTRO/EDIÇÃO DE MEMBRO) C/ FLEXBOX PARA NÃO CORTAR O BOTÃO */}
            {showModal && (
                <div className="modal-overlay" style={{zIndex: 3000}}>
                    <div className="modal" style={{ maxWidth: '550px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', padding: '0', overflow: 'hidden', position: 'relative' }}>
                        
                        {showCropper && (
                            <div style={{position:'absolute', inset:0, background:'#000', zIndex:100, display:'flex', flexDirection:'column'}}>
                                <div style={{position:'relative', flex:1}}>
                                    <Cropper 
                                        image={image} 
                                        crop={crop} 
                                        zoom={zoom} 
                                        aspect={1} 
                                        cropShape="round" 
                                        onCropChange={setCrop} 
                                        onCropComplete={onCropComplete} 
                                        onZoomChange={setZoom} 
                                    />
                                </div>
                                <div style={{background:'#fff', padding:20, display:'flex', gap:15, alignItems:'center'}}>
                                    <input type="range" value={zoom} min={1} max={3} step={0.1} onChange={(e) => setZoom(e.target.value)} style={{flex:1}} />
                                    <button type="button" onClick={finalizarRecorte} className="btn-confirm" style={{background:'#22c55e'}}><Check size={18}/> OK</button>
                                </div>
                            </div>
                        )}

                        {/* Cabeçalho fixo no topo do modal */}
                        <div className="modal-header-flex" style={{ padding: '20px', borderBottom: '1px solid #e2e8f0', margin: 0, flexShrink: 0 }}>
                            <h2 style={{ margin: 0 }}>{editandoId ? 'Editar Cartão' : 'Novo Cartão'}</h2>
                            <button type="button" onClick={fecharModal} className="btn-close-modal-x" style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={24}/></button>
                        </div>

                        {/* Corpo rolável do modal */}
                        <div style={{ overflowY: 'auto', padding: '20px' }} className="custom-scrollbar">
                            <form onSubmit={handleSalvar}>
                                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                                    <div onClick={() => fileInputRef.current.click()} style={{ width: '100px', height: '100px', borderRadius: '50%', margin: '0 auto', border: '2px dashed #ccc', cursor: 'pointer', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
                                        {imagePreview ? <img src={imagePreview} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="preview" /> : <ImageIcon size={30} color="#ccc" />}
                                    </div>
                                    <input type="file" ref={fileInputRef} hidden accept="image/*" onChange={(e) => {
                                        const file = e.target.files[0];
                                        if(file){
                                            const reader = new FileReader();
                                            reader.readAsDataURL(file);
                                            reader.onload = () => { setImage(reader.result); setShowCropper(true); };
                                        }
                                    }} />
                                    <small style={{display:'block', marginTop:5, color:'var(--primary)', fontWeight:'bold', cursor:'pointer'}} onClick={() => fileInputRef.current.click()}>Ajustar Foto</small>
                                </div>

                                {editandoId && (
                                    <div style={{ marginBottom: '20px', padding: '12px', background: novoMembro.ativo ? '#f0fdf4' : '#fef2f2', borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: `1px solid ${novoMembro.ativo ? '#bbf7d0' : '#fecaca'}` }}>
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <span style={{ fontSize: '13px', fontWeight: 'bold', color: novoMembro.ativo ? '#166534' : '#991b1b' }}>Status do Acesso</span>
                                            <span style={{ fontSize: '11px', color: '#64748b' }}>{novoMembro.ativo ? 'Ativo' : 'Bloqueado'}</span>
                                        </div>
                                        <select 
                                            style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px', fontWeight: 'bold' }} 
                                            value={novoMembro.ativo} 
                                            onChange={e => setNovoMembro({ ...novoMembro, ativo: Number(e.target.value) })}
                                        >
                                            <option value={1}>✅ Ativo</option>
                                            <option value={0}>🚫 Inativo</option>
                                        </select>
                                    </div>
                                )}

                                <label className="label">Nome</label>
                                <input className="input" value={novoMembro.nome} onChange={e => setNovoMembro({ ...novoMembro, nome: e.target.value })} required />

                                <label className="label">Whatsapp</label>
                                <input className="input" type="text" value={novoMembro.whatsapp} onChange={e => setNovoMembro({ ...novoMembro, whatsapp: e.target.value })} required />
                                
                                <label className="label">E-mail</label>
                                <input className="input" type="email" value={novoMembro.email} onChange={e => setNovoMembro({ ...novoMembro, email: e.target.value })} required />

                                <div className="input-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                    <div>
                                        <label className="label">Nascimento</label>
                                        <input className="input" type="date" value={novoMembro.data_nascimento} onChange={e => setNovoMembro({ ...novoMembro, data_nascimento: e.target.value })} />
                                    </div>
                                    <div>
                                        <label className="label">Categoria / Função</label>
                                        <select className="input" value={novoMembro.cargo} onChange={e => setNovoMembro({ ...novoMembro, cargo: e.target.value })}>
                                            {categorias.map(cat => (
                                                <option key={cat.id} value={cat.nome}>{cat.nome}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <label className="label">Senha</label>
                                <input 
                                    className="input" 
                                    type="password" 
                                    value={novoMembro.senha} 
                                    onChange={e => setNovoMembro({ ...novoMembro, senha: e.target.value })} 
                                    placeholder={editandoId ? "Vazio mantém atual" : "****"} 
                                    required={!editandoId} 
                                />

                                <button type="submit" className="btn-save-confirm" style={{ width: '100%', marginTop: '20px' }}>
                                    {editandoId ? 'Atualizar Cartão' : 'Emitir Cartão'}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL DE CONFIRMAÇÃO DE EXCLUSÃO */}
            {showDeleteModal && (
                <div className="modal-overlay" style={{ zIndex: 4000 }}>
                    <div className="modal" style={{ maxWidth: '400px', textAlign: 'center', padding: '30px' }}>
                        <div style={{ 
                            background: '#fee2e2', 
                            width: '60px', 
                            height: '60px', 
                            borderRadius: '50%', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            margin: '0 auto 20px' 
                        }}>
                            <Trash2 size={30} color="#ef4444" />
                        </div>
                        
                        <h3 style={{ marginBottom: '10px', color: '#1e293b' }}>Confirmar Exclusão</h3>
                        <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '25px' }}>
                            Tem certeza que deseja remover o acesso de <strong>{itemParaExcluir?.nome}</strong>? 
                            Esta ação não pode ser desfeita.
                        </p>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                            <button 
                                type="button"
                                className="btn-cancel" 
                                onClick={() => setShowDeleteModal(false)}
                                style={{ padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer' }}
                            >
                                Cancelar
                            </button>
                            <button 
                                type="button"
                                onClick={executarExclusao}
                                style={{ 
                                    padding: '10px', 
                                    borderRadius: '8px', 
                                    background: '#ef4444', 
                                    color: '#fff', 
                                    border: 'none', 
                                    fontWeight: 'bold',
                                    cursor: 'pointer'
                                }}
                            >
                                Confirmar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Equipe;